/**
 * Plan-2 Lane D (2026-07-19) — Scheduled Meetings web client.
 *
 * Thin wrappers over backend/src/modules/meetings/scheduled-meetings.routes.ts
 * (mounted at /api/v1/meetings/scheduled):
 *   GET    /meetings/scheduled?scope=own|team|all&from&to&status&mode&limit
 *   GET    /meetings/scheduled/summary?scope=
 *   GET    /meetings/scheduled/:id
 *   POST   /meetings/scheduled            (create — advances lead cold→followup,
 *                                          fires manager invite notification)
 *   PATCH  /meetings/scheduled/:id        (reschedule / status / notes)
 *
 * Role-scope is SERVER-side (exec=own, assistant=all, manager=team, admin=all);
 * we only pass the requested scope — the server silently downgrades when the
 * caller over-asks. Envelope drift at integration is fixed HERE, never in the
 * page (dealerLeadsApi convention).
 */
import { api } from '@/utils/api';
import { joinPersonName } from '@/utils/names';

// ── Types (mirror the route payloads) ───────────────────────────────────────

export type MeetingScope = 'own' | 'team' | 'all';
export type MeetingMode = 'physical' | 'online';
export type MeetingStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show';

export interface MeetingPerson {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
}

export interface MeetingContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone?: string | null;
  city?: string | null;
}

export interface ScheduledMeeting {
  id: string;
  mode: MeetingMode;
  scheduledAt: string; // ISO
  durationMin: number;
  status: MeetingStatus;
  addressLine1?: string | null;
  city?: string | null;
  pincode?: string | null;
  meetingUrl?: string | null;
  notes?: string | null;
  source?: string | null;
  setterId: string;
  setterWillAttend: boolean;
  managerWillJoin: boolean;
  managerId?: string | null;
  attendingTeamId?: string | null;
  contactId?: string | null;
  dealId?: string | null;
  setter?: MeetingPerson | null;
  manager?: MeetingPerson | null;
  contact?: MeetingContact | null;
  deal?: { id: string; title: string } | null;
  attendingTeam?: { id: string; name: string } | null;
}

export interface MeetingsSummary {
  today: number;
  upcoming: number;
  needsAction: number;
  completedThisWeek: number;
}

export interface ListMeetingsParams {
  scope?: MeetingScope;
  from?: string; // ISO
  to?: string; // ISO
  status?: MeetingStatus;
  mode?: MeetingMode;
  contactId?: string;
  dealId?: string;
  limit?: number;
}

export interface CreateMeetingInput {
  contactId?: string;
  dealId?: string;
  mode: MeetingMode;
  scheduledAt: string; // ISO
  durationMin?: number;
  addressLine1?: string;
  city?: string;
  pincode?: string;
  meetingUrl?: string;
  /** SUN-62: make the meeting in the company's own Zoom / Google Meet; meetingUrl then comes back. */
  provider?: 'zoom' | 'google_meet';
  setterWillAttend?: boolean;
  managerWillJoin?: boolean;
  notes?: string;
  source?: 'wrap_up_disposition' | 'meetings_tab' | 'web_admin' | 'lead_detail';
}

export interface UpdateMeetingInput {
  scheduledAt?: string;
  durationMin?: number;
  mode?: MeetingMode;
  addressLine1?: string;
  city?: string;
  pincode?: string;
  meetingUrl?: string;
  managerWillJoin?: boolean;
  status?: MeetingStatus;
  /** '' or null clears the notes. */
  notes?: string | null;
}

// ── Calls ───────────────────────────────────────────────────────────────────

export async function listMeetings(
  params: ListMeetingsParams = {},
): Promise<{ rows: ScheduledMeeting[]; scope: MeetingScope }> {
  const res = await api.get('/meetings/scheduled', { params });
  return {
    rows: (res.data?.data ?? []) as ScheduledMeeting[],
    scope: (res.data?.scope ?? params.scope ?? 'own') as MeetingScope,
  };
}

export async function fetchMeetingsSummary(
  scope: MeetingScope,
): Promise<MeetingsSummary> {
  const res = await api.get('/meetings/scheduled/summary', { params: { scope } });
  const d = res.data?.data ?? {};
  return {
    today: d.today ?? 0,
    upcoming: d.upcoming ?? 0,
    needsAction: d.needsAction ?? 0,
    completedThisWeek: d.completedThisWeek ?? 0,
  };
}

export async function createMeeting(input: CreateMeetingInput): Promise<ScheduledMeeting> {
  const res = await api.post('/meetings/scheduled', input);
  return (res.data?.data ?? res.data) as ScheduledMeeting;
}

export async function updateMeeting(
  id: string,
  input: UpdateMeetingInput,
): Promise<ScheduledMeeting> {
  const res = await api.patch(`/meetings/scheduled/${id}`, input);
  return (res.data?.data ?? res.data) as ScheduledMeeting;
}

// ── Pure helpers (unit-tested in meetings.test.ts) ──────────────────────────

/** Person display name: "First Last" → email → em-dash. */
export function personName(p?: MeetingPerson | MeetingContact | null): string {
  if (!p) return '—';
  // T-051-W — '-' is TRUTHY, so a naive .filter(Boolean) let the backend's
  // "no last name" placeholder through as text. Shared with callStore.ts.
  const name = joinPersonName(p.firstName, p.lastName);
  if (name) return name;
  const email = (p as MeetingPerson).email;
  return email || '—';
}

/** What the meeting is ABOUT — deal title first, else the contact name. */
export function meetingLeadLabel(m: ScheduledMeeting): string {
  if (m.deal?.title?.trim()) return m.deal.title.trim();
  const c = personName(m.contact);
  return c === '—' ? 'Untitled lead' : c;
}

/** One-line location: online URL host, or "address, city pincode". */
export function meetingWhereLabel(m: ScheduledMeeting): string {
  if (m.mode === 'online') {
    if (!m.meetingUrl) return 'Online';
    try {
      return new URL(m.meetingUrl).host;
    } catch {
      return 'Online';
    }
  }
  const parts = [m.addressLine1, m.city, m.pincode].filter(
    (v): v is string => !!v && String(v).trim().length > 0,
  );
  return parts.length ? parts.join(', ') : 'Field visit';
}

/** Tailwind tone classes per status chip (matches the codebase chip style). */
export function statusTone(status: MeetingStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'cancelled':
      return 'bg-gray-100 text-gray-500 border-gray-200';
    case 'no_show':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'scheduled':
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200';
  }
}

export const STATUS_LABELS: Record<MeetingStatus, string> = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

// Meeting times are IST wall-clock (the whole company works in Asia/Kolkata),
// whatever zone this browser is in. India has no daylight saving, so a fixed
// +05:30 offset is exact.
const IST_OFFSET_MIN = 330;

/**
 * datetime-local input value ("YYYY-MM-DDTHH:mm", read as IST) -> ISO string in
 * UTC (or null when empty/invalid). A browser in another zone must not shift it.
 */
export function localInputToIso(value: string | null | undefined): string | null {
  if (!value || !value.trim()) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, sec] = m;
  const utcMs = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(sec ?? 0))
    - IST_OFFSET_MIN * 60_000;
  const out = new Date(utcMs);
  return Number.isNaN(out.getTime()) ? null : out.toISOString();
}

/** ISO -> value for <input type="datetime-local">, showing the IST wall-clock. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ist = new Date(d.getTime() + IST_OFFSET_MIN * 60_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(
    ist.getUTCHours(),
  )}:${pad(ist.getUTCMinutes())}`;
}

/** A meeting still 'scheduled' but in the past needs closing out. */
export function needsAction(m: ScheduledMeeting, now: Date = new Date()): boolean {
  return m.status === 'scheduled' && new Date(m.scheduledAt).getTime() < now.getTime();
}
